import { Suspense, useEffect, useRef, type ReactNode } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'

import { ApiError, apiRequest } from './api/client'
import { rememberReturnTo } from './auth/returnTo'
import { useAuth } from './auth/useAuth'
import { hasStatisticsModuleAccess, isCompanyMasterProfile, type PortalProfile } from './auth/types'
import { hasContainersModuleAccess } from './features/containers'
import { hasRateModelAccess } from './features/rateModel'
import I18nProfileSync from './components/I18nProfileSync'
import UpdateBanner from './components/UpdateBanner'
import { ThemeProvider } from './theme/ThemeContext'
import ThemeProfileSync from './theme/ThemeProfileSync'
import { isExchangeAllowed, type ExchangeKey } from './exchanges/exchangeAvailability'
import { MarketStatusProvider } from './market-status/MarketStatusContext'
import { PartnerOffersBadgeProvider } from './pages/partners/PartnerOffersBadgeContext'
import { ContainersOffersBadgeProvider } from './components/sidebar/ContainersOffersBadgeProvider'
import { SpotSearchAlertsProvider } from './components/sidebar/SpotSearchAlertsProvider'
import GlobalLoader from './shared/loading/GlobalLoader'
import RouteLoadingFallback from './shared/loading/RouteLoadingFallback'
import { lazyWithAssetRecovery } from '@frontend-shared/assets'

const Form = lazyWithAssetRecovery(() => import('./pages/Form'))
const Login = lazyWithAssetRecovery(() => import('./pages/Login'))
const Logintegra = lazyWithAssetRecovery(() => import('./pages/Logintegra'))
const Automations = lazyWithAssetRecovery(() => import('./pages/Automations'))
const Settings = lazyWithAssetRecovery(() => import('./pages/Settings'))
const AiEmail = lazyWithAssetRecovery(() => import('./pages/AiEmail'))
const Trans = lazyWithAssetRecovery(() => import('./pages/Trans'))
const TransRedirect = lazyWithAssetRecovery(() => import('./pages/TransRedirect'))
const EmailRedirect = lazyWithAssetRecovery(() => import('./pages/EmailRedirect'))
const Transporeon = lazyWithAssetRecovery(() => import('./pages/Transporeon'))
const Infracht = lazyWithAssetRecovery(() => import('./pages/Infracht'))
const Azoty = lazyWithAssetRecovery(() => import('./pages/Azoty'))
const CLoad = lazyWithAssetRecovery(() => import('./pages/CLoad'))
const CLoadProcurements = lazyWithAssetRecovery(() => import('./pages/CLoadProcurements'))
const Cersanit = lazyWithAssetRecovery(() => import('./pages/Cersanit'))
const SpotSearch = lazyWithAssetRecovery(() => import('./pages/SpotSearch'))
const PartnersPage = lazyWithAssetRecovery(() => import('./pages/partners/PartnersPage'))
const PartnerIncomingOffersPage = lazyWithAssetRecovery(() => import('./pages/partners/PartnerIncomingOffersPage'))
const PartnerOrdersPage = lazyWithAssetRecovery(() => import('./pages/partners/PartnerOrdersPage'))
const PartnerQuotesPage = lazyWithAssetRecovery(() => import('./pages/partners/PartnerQuotesPage'))
const PartnerAcceptedOrdersPage = lazyWithAssetRecovery(() => import('./pages/partners/PartnerAcceptedOrdersPage'))
const PartnerSettlementsPage = lazyWithAssetRecovery(() => import('./pages/partners/PartnerSettlementsPage'))
const FlowAgent = lazyWithAssetRecovery(() => import('./pages/FlowAgent'))
const Published = lazyWithAssetRecovery(() => import('./pages/Published'))
const DispatcherOpsCenter = lazyWithAssetRecovery(() => import('./pages/DispatcherOpsCenter'))
const DataAgent = lazyWithAssetRecovery(() => import('./pages/DataAgent'))
const CompanyUsers = lazyWithAssetRecovery(() => import('./pages/CompanyUsers'))
const CompanyUsersNew = lazyWithAssetRecovery(() => import('./pages/CompanyUsersNew'))
const Statistics = lazyWithAssetRecovery(() => import('./pages/Statistics'))
const ContainerStatistics = lazyWithAssetRecovery(() => import('./pages/containers/screens/DashboardScreen'))
const RateModel = lazyWithAssetRecovery(() => import('./pages/RateModel'))
const Timocom = lazyWithAssetRecovery(() => import('./pages/Timocom'))
const Teleroute = lazyWithAssetRecovery(() => import('./pages/Teleroute'))
const Econtainers = lazyWithAssetRecovery(() => import('./pages/Econtainers'))
const Containers = lazyWithAssetRecovery(() => import('./pages/Containers'))
const ForgotPassword = lazyWithAssetRecovery(() => import('./pages/ForgotPassword'))
const LicensingAcceptance = lazyWithAssetRecovery(() => import('./pages/LicensingAcceptance'))
const LicensingAccepted = lazyWithAssetRecovery(() => import('./pages/LicensingAccepted'))
const ResetPassword = lazyWithAssetRecovery(() => import('./pages/ResetPassword'))
const ContainerOfferLinkPage = lazyWithAssetRecovery(() => import('./pages/public/ContainerOfferLinkPage'))
const ContainerDriverLinkPage = lazyWithAssetRecovery(() => import('./pages/public/ContainerDriverLinkPage'))

const LIGHTHOUSE_BYPASS_AUTH = import.meta.env.VITE_LIGHTHOUSE_BYPASS_AUTH === 'true'

interface GuardProps {
  children: ReactNode
}

interface IntegrationGuardProps extends GuardProps {
  integrationKey: string
}

interface ExchangeGuardProps extends GuardProps {
  exchangeKey: ExchangeKey
}

const isAbortError = (error: unknown): boolean =>
  error instanceof DOMException
    ? error.name === 'AbortError'
    : error instanceof Error && error.name === 'AbortError'

function RequireAuth({ children }: GuardProps) {
  const { token } = useAuth()
  const location = useLocation()
  if (!token && !LIGHTHOUSE_BYPASS_AUTH) {
    // Cel przekazujemy dwoma kanalami: sessionStorage przezyje nawigacje, `state`
    // przezyje twardy reload z asset recovery. Zapis jest idempotentny, wiec
    // podwojny render w StrictMode niczego nie psuje.
    const from = `${location.pathname}${location.search}${location.hash}`
    rememberReturnTo(from)
    return <Navigate to="/login" replace state={{ from }} />
  }
  return <>{children}</>
}

function RequireIntegration({ children, integrationKey }: IntegrationGuardProps) {
  const { profile } = useAuth()
  if (!profile) {
    return null
  }
  if (isExchangeRoute(integrationKey) && !isExchangeAllowed(profile, integrationKey)) {
    return <Navigate to="/dispatcher-ops" replace />
  }
  if (!profile.integrations?.[integrationKey]) {
    return <Navigate to="/settings" replace />
  }
  return <>{children}</>
}

function RequireExchangeAvailable({ children, exchangeKey }: ExchangeGuardProps) {
  const { profile } = useAuth()
  if (!profile) {
    return null
  }
  if (!isExchangeAllowed(profile, exchangeKey)) {
    return <Navigate to="/dispatcher-ops" replace />
  }
  return <>{children}</>
}

function RequireCompanyMaster({ children }: GuardProps) {
  const { profile } = useAuth()
  if (!profile) {
    return null
  }
  if (!isCompanyMasterProfile(profile)) {
    return <Navigate to="/dispatcher-ops" replace />
  }
  return <>{children}</>
}

function RequireStatisticsAccess({ children }: GuardProps) {
  const { profile } = useAuth()
  if (!profile) {
    return null
  }
  if (!hasStatisticsModuleAccess(profile)) {
    return <Navigate to="/dispatcher-ops" replace />
  }
  return <>{children}</>
}

// Model stawek: flaga FIRMOWA features.rateModel z /api/me — company master jej NIE omija
// (inaczej niż per-user modules.statistics w RequireStatisticsAccess powyżej).
function RequireRateModelAccess({ children }: GuardProps) {
  const { profile } = useAuth()
  if (!profile) {
    return null
  }
  if (!hasRateModelAccess(profile)) {
    return <Navigate to="/dispatcher-ops" replace />
  }
  return <>{children}</>
}

// Moduł Kontenery: flaga FIRMOWA features.containers z /api/me (kill-switch app.containers.enabled AND
// containers_module_flags) — company master jej NIE omija. Twardą granicą jest 403 z /api/containers/**.
function RequireContainersAccess({ children }: GuardProps) {
  const { profile } = useAuth()
  if (!profile) {
    return null
  }
  if (!hasContainersModuleAccess(profile)) {
    return <Navigate to="/dispatcher-ops" replace />
  }
  return <>{children}</>
}

// Stary adres Pulpitu kierownika (otwarte karty, zakładki przeglądarki): od 20.10.2026 to Statystyki kontenerowe w Statystykach firmy.
// Bez strażnika modułu — o dostępie decyduje nowa trasa; zapytanie (?period, ?lang) idzie dalej.
function ContainersDashboardRedirect() {
  const { search } = useLocation()
  return <Navigate to={{ pathname: '/statistics/containers', search }} replace />
}

const isExchangeRoute = (value: string): value is ExchangeKey =>
  value === 'trans' || value === 'timocom' || value === 'teleroute' || value === 'econtainers'

export default function App() {
  const { token, profile, setAuth, clearAuth } = useAuth()
  const profileSyncTokenRef = useRef<string | null>(null)

  useEffect(() => {
    if (!token) {
      profileSyncTokenRef.current = null
      return
    }
    if (profileSyncTokenRef.current === token) {
      return
    }
    profileSyncTokenRef.current = token

    const controller = new AbortController()
    const hadStoredProfile = Boolean(profile)
    let settled = false
    const loadProfile = async () => {
      try {
        const data = await apiRequest<PortalProfile>('/api/me', {
          token,
          signal: controller.signal,
          cacheKey: 'me',
          cacheTtlMs: 10000,
        })
        settled = true
        setAuth(token, data)
      } catch (error: unknown) {
        if (isAbortError(error)) {
          return
        }
        settled = true
        if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
          clearAuth()
          return
        }
        if (!hadStoredProfile) {
          clearAuth()
        }
      }
    }

    void loadProfile()
    return () => {
      // Sprzątanie w trakcie lotu (profil zmieniony z innego miejsca albo podwójny efekt StrictMode):
      // przerywamy i ZWALNIAMY znacznik, żeby kolejny przebieg efektu ponowił odświeżenie.
      // Po rozstrzygnięciu (sukces albo błąd) nic nie ruszamy — jedno /api/me na token.
      if (!settled) {
        controller.abort()
        profileSyncTokenRef.current = null
      }
    }
  }, [clearAuth, profile, setAuth, token])

  return (
    <>
      <GlobalLoader />
      <UpdateBanner />
      <Suspense fallback={<RouteLoadingFallback />}>
        <ThemeProvider>
        <MarketStatusProvider token={token}>
         <PartnerOffersBadgeProvider>
         <SpotSearchAlertsProvider>
         <ContainersOffersBadgeProvider>
          <I18nProfileSync />
          <ThemeProfileSync />
          <Routes>
            <Route path="/" element={<Navigate to={token ? '/dispatcher-ops' : '/login'} replace />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/contracts/accept/:token" element={<LicensingAcceptance />} />
            <Route path="/licensing/accept/:token" element={<LicensingAcceptance />} />
            <Route path="/contracts/accepted/:reference" element={<LicensingAccepted />} />
            <Route path="/licensing/accepted/:reference" element={<LicensingAccepted />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/c/offer/:token" element={<ContainerOfferLinkPage />} />
            <Route path="/c/driver/:token" element={<ContainerDriverLinkPage />} />
            <Route
              path="/dashboard"
              element={
                <RequireAuth>
                  <Navigate to="/dispatcher-ops" replace />
                </RequireAuth>
              }
            />
            <Route
              path="/logintegra"
              element={
                <RequireAuth>
                  <RequireIntegration integrationKey="logintegra">
                    <Logintegra />
                  </RequireIntegration>
                </RequireAuth>
              }
            />
            <Route
              path="/form"
              element={
                <RequireAuth>
                  <Form />
                </RequireAuth>
              }
            />
            <Route
              path="/automations"
              element={
                <RequireAuth>
                  <Automations />
                </RequireAuth>
              }
            />
            <Route
              path="/trans"
              element={
                <RequireAuth>
                  <RequireIntegration integrationKey="trans">
                    <Trans />
                  </RequireIntegration>
                </RequireAuth>
              }
            />
            <Route
              path="/tms-connect"
              element={
                <RequireAuth>
                  <Navigate to="/dispatcher-ops" replace />
                </RequireAuth>
              }
            />
            <Route
              path="/spot-search"
              element={
                <RequireAuth>
                  <SpotSearch />
                </RequireAuth>
              }
            />
            <Route
              path="/autopilot"
              element={
                <RequireAuth>
                  <Navigate to="/dispatcher-ops" replace />
                </RequireAuth>
              }
            />
            <Route
              path="/fleet-autopilot"
              element={
                <RequireAuth>
                  <Navigate to="/dispatcher-ops" replace />
                </RequireAuth>
              }
            />
            <Route
              path="/flow-agent"
              element={
                <RequireAuth>
                  <FlowAgent />
                </RequireAuth>
              }
            />
            <Route
              path="/published"
              element={
                <RequireAuth>
                  <Published />
                </RequireAuth>
              }
            />
            <Route
              path="/dispatcher-ops"
              element={
                <RequireAuth>
                  <DispatcherOpsCenter />
                </RequireAuth>
              }
            />
            <Route
              path="/data-agent"
              element={
                <RequireAuth>
                  <DataAgent />
                </RequireAuth>
              }
            />
            <Route
              path="/timocom"
              element={
                <RequireAuth>
                  <RequireExchangeAvailable exchangeKey="timocom">
                    <Timocom />
                  </RequireExchangeAvailable>
                </RequireAuth>
              }
            />
            <Route
              path="/teleroute"
              element={
                <RequireAuth>
                  <RequireExchangeAvailable exchangeKey="teleroute">
                    <Teleroute />
                  </RequireExchangeAvailable>
                </RequireAuth>
              }
            />
            <Route
              path="/econtainers"
              element={
                <RequireAuth>
                  <RequireExchangeAvailable exchangeKey="econtainers">
                    <Econtainers />
                  </RequireExchangeAvailable>
                </RequireAuth>
              }
            />
            <Route
              path="/transporeon"
              element={
                <RequireAuth>
                  <RequireIntegration integrationKey="transporeon">
                    <Transporeon />
                  </RequireIntegration>
                </RequireAuth>
              }
            />
            <Route
              path="/infracht"
              element={
                <RequireAuth>
                  <RequireIntegration integrationKey="infracht">
                    <Infracht />
                  </RequireIntegration>
                </RequireAuth>
              }
            />
            <Route
              path="/azoty"
              element={
                <RequireAuth>
                  <RequireIntegration integrationKey="azoty">
                    <Azoty />
                  </RequireIntegration>
                </RequireAuth>
              }
            />
            <Route
              path="/cload"
              element={
                <RequireAuth>
                  <RequireIntegration integrationKey="cload">
                    <CLoad />
                  </RequireIntegration>
                </RequireAuth>
              }
            />
            <Route
              path="/cload/procurements"
              element={
                <RequireAuth>
                  <RequireIntegration integrationKey="cload">
                    <CLoadProcurements />
                  </RequireIntegration>
                </RequireAuth>
              }
            />
            <Route
              path="/cersanit"
              element={
                <RequireAuth>
                  <Cersanit />
                </RequireAuth>
              }
            />
            <Route
              path="/email"
              element={
                <RequireAuth>
                  <AiEmail />
                </RequireAuth>
              }
            />
            <Route
              path="/order-verification"
              element={
                <RequireAuth>
                  <Navigate to="/dispatcher-ops" replace />
                </RequireAuth>
              }
            />
            <Route
              path="/settings"
              element={
                <RequireAuth>
                  <Settings />
                </RequireAuth>
              }
            />
            <Route
              path="/company-users"
              element={
                <RequireAuth>
                  <RequireCompanyMaster>
                    <CompanyUsers />
                  </RequireCompanyMaster>
                </RequireAuth>
              }
            />
            <Route
              path="/company-users/new"
              element={
                <RequireAuth>
                  <RequireCompanyMaster>
                    <CompanyUsersNew />
                  </RequireCompanyMaster>
                </RequireAuth>
              }
            />
            <Route
              path="/statistics"
              element={
                <RequireAuth>
                  <RequireStatisticsAccess>
                    <Statistics />
                  </RequireStatisticsAccess>
                </RequireAuth>
              }
            />
            {/* Statystyki kontenerowe: dostęp jak do statystyk; flagę modułu firmy sprawdza ekran (bez niej wraca do /statistics).
                ponytail: stary link firmy bez modułu ładuje chunk ekranu przed przekierowaniem; strażnik trasy, gdy zacznie to ważyć. */}
            <Route
              path="/statistics/containers"
              element={
                <RequireAuth>
                  <RequireStatisticsAccess>
                    <ContainerStatistics />
                  </RequireStatisticsAccess>
                </RequireAuth>
              }
            />
            <Route path="/containers/dashboard" element={<ContainersDashboardRedirect />} />
            <Route
              path="/rate-model"
              element={
                <RequireAuth>
                  <RequireRateModelAccess>
                    <RateModel />
                  </RequireRateModelAccess>
                </RequireAuth>
              }
            />
            <Route
              path="/containers/*"
              element={
                <RequireAuth>
                  <RequireContainersAccess>
                    <Containers />
                  </RequireContainersAccess>
                </RequireAuth>
              }
            />
            <Route
              path="/partner-settlements"
              element={
                <RequireAuth>
                  <RequireCompanyMaster>
                    <PartnerSettlementsPage />
                  </RequireCompanyMaster>
                </RequireAuth>
              }
            />
            <Route
              path="/partners"
              element={
                <RequireAuth>
                  <RequireCompanyMaster>
                    <PartnersPage />
                  </RequireCompanyMaster>
                </RequireAuth>
              }
            />
            <Route
              path="/partners/incoming"
              element={
                <RequireAuth>
                  <PartnerIncomingOffersPage />
                </RequireAuth>
              }
            />
            <Route
              path="/partners/quoted"
              element={
                <RequireAuth>
                  <PartnerIncomingOffersPage quotedOnly />
                </RequireAuth>
              }
            />
            <Route
              path="/partners/orders"
              element={
                <RequireAuth>
                  <PartnerAcceptedOrdersPage />
                </RequireAuth>
              }
            />
            <Route
              path="/orders"
              element={
                <RequireAuth>
                  <PartnerOrdersPage />
                </RequireAuth>
              }
            />
            <Route
              path="/partners/quotes"
              element={
                <RequireAuth>
                  <PartnerQuotesPage />
                </RequireAuth>
              }
            />
            <Route path="/trans_redirect_uri" element={<TransRedirect />} />
            <Route path="/email_redirect_uri" element={<EmailRedirect />} />
            <Route path="*" element={<Navigate to={token ? '/dispatcher-ops' : '/login'} replace />} />
          </Routes>
         </ContainersOffersBadgeProvider>
         </SpotSearchAlertsProvider>
         </PartnerOffersBadgeProvider>
        </MarketStatusProvider>
        </ThemeProvider>
      </Suspense>
    </>
  )
}
